/**
 * MUI theme built from the design tokens (see `./tokens.ts`).
 *
 * Mapping (guide 9.5 → MUI):
 *   --action        → palette.primary.main      (the single action color)
 *   --text-*        → palette.text.*
 *   --canvas        → palette.background.default
 *   --surface       → palette.background.paper
 *   --border-hairline → palette.divider
 *   positive/warning/negative → palette.success/warning/error
 *
 * Transition rule (T1): `palette.secondary` no longer paints UI in the new
 * screens, but ~550 legacy usages still read it, most of them as a strong
 * accent with white text on top. It maps to the deep green so those screens
 * stay readable and on-brand until they are swept.
 */
import { alpha, createTheme, Theme } from '@mui/material/styles';

import {
  brand,
  buildTokens,
  DesignTokens,
  fastTransition,
  tokensToCssVars,
} from './tokens';

interface ClientPalette {
  primary: {
    main: string;
  };
  secondary: {
    main: string;
  };
}

declare module '@mui/material/styles/createPalette' {
  interface CommonColors {
    grey: string;
    danger: string;
  }
  interface Palette {
    select: {
      hover: string;
    };
    tokens: DesignTokens;
  }
  interface PaletteOptions {
    select?: {
      hover?: string;
    };
    tokens?: DesignTokens;
  }
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xxl: true;
    xxxl: true;
  }
  interface Components {
    MuiPickersPopper: {
      styleOverrides: {
        root: {
          '& .MuiDialogActions-root': {
            '& .MuiButtonBase-root': {
              width: string;
              height: string;
            };
          };
        };
      };
    };
    MuiPickersYear: {
      styleOverrides: {
        yearButton: {
          '&.Mui-selected': {
            backgroundColor: string;
            '&:focus': {
              backgroundColor: string;
            };
          };
        };
      };
    };
    MuiMultiSectionDigitalClock: {
      styleOverrides: {
        root: {
          '& .MuiMenuItem-root': {
            '&.Mui-selected': {
              backgroundColor: string;
              '&:hover': {
                backgroundColor: string;
              };
            };
            '&:hover': {
              backgroundColor: string;
            };
          };
          '& .MuiList-root': {
            marginRight: string;
            ':after': {
              height: string;
            };
            '&::-webkit-scrollbar': {
              width: string;
            };
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: string;
              borderRadius: string;
              '&:hover': {
                backgroundColor: string;
              };
            };
          };
        };
      };
    };
  }
}

export const getTheme = (clientPalette: ClientPalette): Theme => {
  const tokens = buildTokens(clientPalette.primary.main);
  const { color, font, type, radius, shadow, motion } = tokens;
  const cssVars = tokensToCssVars(tokens);

  const typeStyle = (
    key: keyof DesignTokens['type']
  ): Record<string, unknown> => ({
    fontSize: `${type[key].size}px`,
    lineHeight: type[key].lineHeight,
    fontWeight: type[key].weight,
    letterSpacing: type[key].tracking,
  });

  return createTheme({
    palette: {
      tokens,
      primary: {
        main: color.action,
        light: color.brand300,
        dark: color.actionActive,
        contrastText: color.actionFg,
      },
      secondary: {
        main: brand.deepGreen,
        light: color.brand800,
        dark: color.n950,
        contrastText: brand.white,
      },
      success: { main: color.positive, light: color.positiveTint },
      warning: { main: color.warning, light: color.warningTint },
      error: { main: color.negative, light: color.negativeTint },
      text: {
        primary: color.textPrimary,
        secondary: color.textSecondary,
        disabled: color.textTertiary,
      },
      background: {
        default: color.canvas,
        paper: color.surface,
      },
      divider: color.borderHairline,
      action: {
        hover: color.surfaceHover,
        selected: color.actionTint,
        focus: alpha(color.action, 0.24),
      },
      common: {
        white: brand.white,
        black: color.n950,
        grey: color.textSecondary,
        danger: color.negative,
      },
      select: {
        hover: color.surfaceHover,
      },
    },
    shape: {
      borderRadius: radius.control,
    },
    typography: {
      fontFamily: font.family,
      fontWeightRegular: font.weightRegular,
      fontWeightMedium: font.weightMedium,
      fontWeightBold: font.weightMedium,
      allVariants: {
        color: color.textPrimary,
      },
      caption: typeStyle('caption'),
      subtitle2: typeStyle('label'),
      body2: typeStyle('ui'),
      body1: typeStyle('body'),
      subtitle1: typeStyle('body'),
      h6: typeStyle('titleSm'),
      h5: typeStyle('title'),
      h4: typeStyle('pageTitle'),
      h3: typeStyle('moneyHero'),
      h2: typeStyle('display'),
      h1: typeStyle('display'),
      button: {
        ...typeStyle('ui'),
        fontWeight: font.weightMedium,
        textTransform: 'none',
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1600,
        xxl: 1920,
        xxxl: 3840,
      },
    },
    transitions: {
      easing: {
        easeOut: motion.easeOut,
        easeInOut: motion.easeOut,
        sharp: motion.easeOut,
      },
      duration: {
        shortest: 120,
        shorter: 120,
        short: 180,
        standard: 180,
        complex: 240,
        enteringScreen: 240,
        leavingScreen: 180,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': cssVars,
          html: {
            height: '100%',
            fontFamily: font.family,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          body: {
            backgroundColor: color.canvas,
            color: color.textPrimary,
            fontSize: `${type.ui.size}px`,
            lineHeight: type.ui.lineHeight,
          },
          // Inputs are ringed by their MUI root (`.Mui-focused`); ringing the
          // inner element too produced a double outline.
          ':focus-visible:not(input):not(textarea):not(select):not(.MuiInputBase-input)':
            {
              outline: 'none',
              boxShadow: color.focusRing,
            },
          'input:focus-visible, textarea:focus-visible, select:focus-visible': {
            outline: 'none',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '*': {
              transitionDuration: '0ms !important',
              animation: 'none !important',
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          variant: 'contained',
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            minHeight: '36px',
            padding: '0 16px',
            borderRadius: `${radius.control}px`,
            fontSize: `${type.ui.size}px`,
            lineHeight: type.ui.lineHeight,
            fontWeight: font.weightMedium,
            letterSpacing: type.ui.tracking,
            textTransform: 'none',
            boxShadow: 'none',
            gap: '8px',
            transition: fastTransition(
              motion,
              'background-color',
              'border-color',
              'color'
            ),
            '&:hover': { boxShadow: 'none' },
            '&.Mui-focusVisible': { boxShadow: color.focusRing },
            '& .material-symbols-outlined': { fontSize: '18px' },
          },
          contained: {
            backgroundColor: color.action,
            color: color.actionFg,
            '&:hover': { backgroundColor: color.actionHover },
            '&:active': { backgroundColor: color.actionActive },
            '&.Mui-disabled': {
              backgroundColor: color.n150,
              color: color.textTertiary,
            },
          },
          containedSecondary: {
            backgroundColor: brand.deepGreen,
            color: brand.white,
            '&:hover': { backgroundColor: color.brand900 },
          },
          outlined: {
            backgroundColor: color.surface,
            color: color.textPrimary,
            border: `1px solid ${color.borderHairline}`,
            '&:hover': {
              backgroundColor: color.surfaceHover,
              borderColor: color.borderStrong,
            },
            '&.Mui-disabled': {
              color: color.textTertiary,
              borderColor: color.borderHairline,
            },
          },
          text: {
            color: color.textSecondary,
            '&:hover': {
              color: color.textPrimary,
              backgroundColor: color.surfaceHover,
            },
          },
          sizeSmall: { minHeight: '32px', padding: '0 12px' },
          sizeLarge: {
            minHeight: '44px',
            padding: '0 20px',
            fontSize: `${type.body.size}px`,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: `${radius.control}px`,
            color: color.textSecondary,
            '&:hover': {
              backgroundColor: color.surfaceHover,
              color: color.textPrimary,
            },
            '&.Mui-focusVisible': { boxShadow: color.focusRing },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            minHeight: '40px',
            borderRadius: `${radius.control}px`,
            backgroundColor: color.surface,
            fontSize: `${type.ui.size}px`,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: color.borderHairline,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: color.borderStrong,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: color.action,
              borderWidth: '1px',
            },
            '&.Mui-focused': { boxShadow: color.focusRing },
            '&.Mui-focused:has(legend > span:not(.notranslate))': {
              boxShadow: 'none',
              '& .MuiOutlinedInput-notchedOutline': { borderWidth: '2px' },
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: color.negative,
            },
          },
          // the textarea already carries the 9px 12px below; MUI's root
          // padding on top of it pushed the text away from the label
          multiline: { padding: 0 },
          input: {
            padding: '9px 12px',
            '&::placeholder': { color: color.textTertiary, opacity: 1 },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            ...typeStyle('label'),
            color: color.textSecondary,
            '&.Mui-focused': { color: color.textSecondary },
            '&.Mui-error': { color: color.negative },
          },
          outlined: {
            '&:not(.MuiInputLabel-shrink)': {
              transform: 'translate(12px, 11px) scale(1)',
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            ...typeStyle('caption'),
            marginLeft: 0,
            color: color.textSecondary,
            '&.Mui-error': { color: color.negative },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${color.borderHairline}`,
            padding: '0 16px',
            height: '48px',
            fontSize: `${type.ui.size}px`,
            lineHeight: type.ui.lineHeight,
            color: color.textPrimary,
          },
          head: {
            ...typeStyle('label'),
            color: color.textSecondary,
            backgroundColor: 'transparent',
            textTransform: 'none',
            whiteSpace: 'nowrap',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: `background-color ${motion.fast} ${motion.easeOut}`,
            '&.MuiTableRow-hover:hover': {
              backgroundColor: color.surfaceHover,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            height: '28px',
            borderRadius: `${radius.pill}px`,
            fontSize: `${type.caption.size}px`,
            fontWeight: font.weightMedium,
            letterSpacing: type.caption.tracking,
          },
          outlined: {
            borderColor: color.borderHairline,
            backgroundColor: color.surface,
            color: color.textSecondary,
            '&:hover': { backgroundColor: color.surfaceHover },
          },
          filledPrimary: {
            backgroundColor: color.actionTint,
            color: color.textPrimary,
            '&:hover': { backgroundColor: color.brand100 },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
          rounded: { borderRadius: `${radius.card}px` },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: `${radius.modal}px`,
            boxShadow: shadow.modal,
            border: `1px solid ${color.borderHairline}`,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: color.overlay,
            '&.MuiBackdrop-invisible': { backgroundColor: 'transparent' },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            boxShadow: shadow.float,
            borderLeft: `1px solid ${color.borderHairline}`,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: `${radius.card}px`,
            border: `1px solid ${color.borderHairline}`,
            boxShadow: shadow.float,
            marginTop: '4px',
          },
          list: { padding: '4px' },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            minHeight: '36px',
            borderRadius: `${radius.control}px`,
            fontSize: `${type.ui.size}px`,
            '&:hover': { backgroundColor: color.surfaceHover },
            '&.Mui-selected': {
              backgroundColor: color.actionTint,
              '&:hover': { backgroundColor: color.brand100 },
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            borderRadius: `${radius.card}px`,
            border: `1px solid ${color.borderHairline}`,
            boxShadow: shadow.float,
            marginTop: '4px',
          },
          listbox: {
            padding: '4px',
            '& .MuiAutocomplete-option': {
              minHeight: '36px',
              padding: '6px 16px',
              borderRadius: `${radius.control}px`,
              fontSize: `${type.ui.size}px`,
              '&.Mui-focused': { backgroundColor: color.surfaceHover },
              '&[aria-selected="true"]': {
                backgroundColor: color.actionTint,
                '&.Mui-focused': { backgroundColor: color.brand100 },
              },
            },
          },
          noOptions: { fontSize: `${type.ui.size}px` },
          loading: { fontSize: `${type.ui.size}px` },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            ...typeStyle('caption'),
            backgroundColor: color.n900,
            color: brand.white,
            borderRadius: `${radius.control}px`,
            padding: '6px 10px',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontSize: `${type.ui.size}px`,
            fontWeight: font.weightRegular,
            color: color.textSecondary,
            minHeight: '44px',
            padding: '10px 14px',
            '&.Mui-selected': {
              color: color.textPrimary,
              fontWeight: font.weightMedium,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { backgroundColor: color.action, height: '2px' },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: color.n100,
            borderRadius: `${radius.control}px`,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { backgroundColor: color.n150, borderRadius: '2px' },
          bar: { backgroundColor: color.action },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          colorPrimary: { color: color.action },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': {
              color: brand.white,
              '& + .MuiSwitch-track': {
                backgroundColor: color.brand800,
                opacity: 1,
              },
              '&.Mui-disabled': {
                color: brand.white,
                '& + .MuiSwitch-track': {
                  backgroundColor: color.brand800,
                  opacity: 0.5,
                },
              },
            },
            '&.Mui-disabled': {
              '& + .MuiSwitch-track': {
                opacity: 0.3,
              },
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: color.borderStrong,
            borderRadius: `${radius.check}px`,
            '&.Mui-checked': { color: color.brand800 },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            ...typeStyle('ui'),
            color: color.textPrimary,
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            fontFamily: font.family,
            '&.MuiPickersDay-root': {
              '&.MuiPickersDay-today': {
                backgroundColor: 'transparent',
                borderColor: color.borderStrong,
              },
              '&:active': {
                backgroundColor: color.actionTint,
              },
              '&.Mui-selected': {
                backgroundColor: color.brand800,
                color: brand.white,
                ':focus': {
                  backgroundColor: color.brand800,
                },
              },
            },
          },
        },
      },
      MuiPickersYear: {
        styleOverrides: {
          yearButton: {
            '&.Mui-selected': {
              backgroundColor: color.brand800,
              '&:focus': {
                backgroundColor: color.brand800,
              },
            },
          },
        },
      },
      MuiPickersPopper: {
        styleOverrides: {
          root: {
            '& .MuiDialogActions-root': {
              '& .MuiButtonBase-root': {
                width: '140px',
                height: '36px',
              },
            },
          },
        },
      },
      MuiMultiSectionDigitalClock: {
        styleOverrides: {
          root: {
            '& .MuiList-root': {
              marginRight: '10px',
              ':after': {
                height: '20px',
              },
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: color.n300,
                borderRadius: '10px',
                '&:hover': {
                  backgroundColor: color.n400,
                },
              },
            },
            '& .MuiMenuItem-root': {
              '&.Mui-selected': {
                backgroundColor: color.actionTint,
                '&:hover': {
                  backgroundColor: color.brand100,
                },
              },
              '&:hover': {
                backgroundColor: color.surfaceHover,
              },
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: 'inherit',
          },
        },
      },
    },
  });
};
